import { useEffect, useState } from 'react';
import { settingsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { SiteFooter } from '../components/SiteFooter';
import { ImageUpload, GalleryUpload } from '../components/ImageUpload';
import {
  Loader2,
  Save,
  Building2,
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Globe,
  Eye,
  CreditCard,
  Image as ImageIcon,
  Camera,
  FileText,
  MessageCircle,
} from 'lucide-react';

const defaultState = {
  company_name: '',
  legal_name: '',
  company_tagline: '',
  institutional_text: '',
  cnpj: '',
  tax_id: '',
  address: '',
  city: '',
  state: '',
  cep: '',
  phone: '',
  whatsapp: '',
  email: '',
  instagram: '',
  facebook: '',
  website: '',
  copyright_text: '',
  show_payment_info: true,
  payment_info_text: '',
  logo_url: '',
  banner_url: '',
  card_url: '',
  photos: [],
  whatsapp_central: '',
  header_logo_url: '',
  header_logo_horizontal_url: '',
  hero_image_url: '',
  hero_title: '',
  hero_subtitle: '',
  hero_tagline: '',
};

export const FooterSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultState);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    settingsAPI
      .getFooter()
      .then(({ data }) => setForm({ ...defaultState, ...data }))
      .catch(() => toast.error('Não foi possível carregar as configurações.'))
      .finally(() => setLoading(false));
  }, []);

  const update = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsAPI.updateFooter(form);
      toast.success('Rodapé atualizado com sucesso!');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="footer-settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dados da Empresa</h1>
        <p className="text-slate-500 mt-1">
          Configure a identidade visual, dados cadastrais e o rodapé do marketplace público.
          As alterações são aplicadas em tempo real, sem precisar mexer em código.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 pb-32">
        {/* Visual Identity - Logo / Banner / Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-blue-600" />
              Identidade Visual
            </CardTitle>
            <CardDescription>
              Clique em cada imagem para fazer upload. As mudanças são aplicadas imediatamente.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <ImageUpload
                  value={form.logo_url}
                  onChange={(url) => update('logo_url')(url)}
                  folder="company-logo"
                  shape="square"
                  placeholder="Logo da empresa"
                  testId="upload-logo"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">Logo (quadrada, ideal 512×512)</p>
              </div>
              <div>
                <ImageUpload
                  value={form.banner_url}
                  onChange={(url) => update('banner_url')(url)}
                  folder="company-banner"
                  shape="wide"
                  placeholder="Banner principal"
                  testId="upload-banner"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">Banner (proporção 3:1)</p>
              </div>
              <div>
                <ImageUpload
                  value={form.card_url}
                  onChange={(url) => update('card_url')(url)}
                  folder="company-card"
                  shape="tall"
                  placeholder="Card de divulgação"
                  testId="upload-card"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">Card (proporção 3:4)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Photo Gallery */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Cabeçalho & Banner Principal
            </CardTitle>
            <CardDescription>Logos do cabeçalho e banner institucional exibido na home do marketplace</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <ImageUpload
                  value={form.header_logo_url}
                  onChange={(url) => update('header_logo_url')(url)}
                  folder="company-header-logo"
                  shape="square"
                  placeholder="Logo do cabeçalho (quadrada)"
                  testId="upload-header-logo"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">Logo Cabeçalho (quadrada, ideal 512×512)</p>
              </div>
              <div>
                <ImageUpload
                  value={form.header_logo_horizontal_url}
                  onChange={(url) => update('header_logo_horizontal_url')(url)}
                  folder="company-header-horizontal"
                  shape="wide"
                  placeholder="Logo horizontal (banner estreito)"
                  testId="upload-header-horizontal"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">Logo Horizontal (proporção 4:1)</p>
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100 space-y-4">
              <div>
                <ImageUpload
                  value={form.hero_image_url}
                  onChange={(url) => update('hero_image_url')(url)}
                  folder="company-hero"
                  shape="wide"
                  placeholder="Banner Principal (Home)"
                  testId="upload-hero"
                />
                <p className="text-xs text-slate-500 mt-2 text-center">Banner institucional da home (3:1)</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hero_title">Título do Banner</Label>
                  <Input id="hero_title" value={form.hero_title} onChange={update('hero_title')} placeholder="DESTACK VENDA MAIS" data-testid="input-hero-title" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hero_tagline">Tagline (linha curta)</Label>
                  <Input id="hero_tagline" value={form.hero_tagline} onChange={update('hero_tagline')} placeholder="Tecnologia • Agilidade • Resultados" data-testid="input-hero-tagline" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hero_subtitle">Subtítulo</Label>
                <Input id="hero_subtitle" value={form.hero_subtitle} onChange={update('hero_subtitle')} placeholder="O Marketplace Inteligente para Comprar e Vender" data-testid="input-hero-subtitle" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Photo Gallery */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Galeria de Fotos
            </CardTitle>
            <CardDescription>Fotos da empresa, equipe, eventos ou produtos em destaque</CardDescription>
          </CardHeader>
          <CardContent>
            <GalleryUpload
              value={form.photos || []}
              onChange={(photos) => update('photos')(photos)}
              folder="company-gallery"
              max={12}
              testId="upload-gallery"
            />
          </CardContent>
        </Card>

        {/* Institutional */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Identidade da Empresa
            </CardTitle>
            <CardDescription>Nome fantasia, slogan e texto institucional</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Nome fantasia</Label>
                <Input id="company_name" value={form.company_name} onChange={update('company_name')} data-testid="input-company-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="legal_name">Razão social</Label>
                <Input
                  id="legal_name"
                  placeholder="Nome jurídico da empresa"
                  value={form.legal_name}
                  onChange={update('legal_name')}
                  data-testid="input-legal-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_tagline">Slogan / Tagline</Label>
              <Input id="company_tagline" placeholder="Filmagem • Criação • Edição" value={form.company_tagline} onChange={update('company_tagline')} data-testid="input-tagline" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="institutional_text">Texto institucional</Label>
              <Textarea
                id="institutional_text"
                rows={3}
                placeholder="Breve descrição da empresa exibida no rodapé"
                value={form.institutional_text}
                onChange={update('institutional_text')}
                data-testid="input-institutional"
              />
            </div>
          </CardContent>
        </Card>

        {/* Legal / Fiscal */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Dados Jurídicos
            </CardTitle>
            <CardDescription>CNPJ e inscrições — usados em documentos e rodapé</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input id="cnpj" placeholder="00.000.000/0000-00" value={form.cnpj} onChange={update('cnpj')} data-testid="input-cnpj" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_id">Inscrição Estadual / Outra</Label>
                <Input id="tax_id" placeholder="ISENTO ou número" value={form.tax_id} onChange={update('tax_id')} data-testid="input-tax-id" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Endereço
            </CardTitle>
            <CardDescription>Localização exibida no rodapé</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Endereço (rua, número, bairro)</Label>
              <Input id="address" value={form.address} onChange={update('address')} data-testid="input-address" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">Cidade</Label>
                <Input id="city" value={form.city} onChange={update('city')} data-testid="input-city" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">Estado (UF)</Label>
                <Input id="state" maxLength={2} value={form.state} onChange={update('state')} data-testid="input-state" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cep">CEP</Label>
                <Input id="cep" placeholder="00000-000" value={form.cep} onChange={update('cep')} data-testid="input-cep" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              Contato
            </CardTitle>
            <CardDescription>Canais para os clientes entrarem em contato</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input id="phone" placeholder="(00) 0000-0000" value={form.phone} onChange={update('phone')} data-testid="input-phone" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp">WhatsApp</Label>
                <Input id="whatsapp" placeholder="(00) 00000-0000" value={form.whatsapp} onChange={update('whatsapp')} data-testid="input-whatsapp" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="email" type="email" className="pl-10" value={form.email} onChange={update('email')} data-testid="input-email" />
              </div>
            </div>
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <Label htmlFor="whatsapp_central" className="flex items-center gap-2 text-emerald-700">
                <MessageCircle className="w-4 h-4" />
                WhatsApp Central DESTACK (recebe todos os pedidos)
              </Label>
              <Input
                id="whatsapp_central"
                placeholder="(65) 99999-9999"
                value={form.whatsapp_central}
                onChange={update('whatsapp_central')}
                data-testid="input-whatsapp-central"
              />
              <p className="text-xs text-slate-500">
                Número usado para receber os pedidos do marketplace via WhatsApp.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Social */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Redes Sociais & Web
            </CardTitle>
            <CardDescription>Links de redes sociais (deixe em branco para esconder)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="instagram" className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-500" /> Instagram (usuário ou URL)
              </Label>
              <Input id="instagram" placeholder="@destackvendasmais" value={form.instagram} onChange={update('instagram')} data-testid="input-instagram" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="facebook" className="flex items-center gap-2">
                <Facebook className="w-4 h-4 text-blue-600" /> Facebook (usuário ou URL)
              </Label>
              <Input id="facebook" placeholder="destackvendasmais" value={form.facebook} onChange={update('facebook')} data-testid="input-facebook" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website" className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-600" /> Site
              </Label>
              <Input id="website" placeholder="destackvendamais.com.br" value={form.website} onChange={update('website')} data-testid="input-website" />
            </div>
          </CardContent>
        </Card>

        {/* Bottom bar + Trust */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Faixa de Confiança & Direitos
            </CardTitle>
            <CardDescription>Linha superior do rodapé e direitos reservados</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <p className="font-medium text-sm">Mostrar faixa de pagamentos</p>
                <p className="text-xs text-slate-500">Exibe um aviso destacado de meio de pagamento no topo do rodapé.</p>
              </div>
              <Switch
                checked={!!form.show_payment_info}
                onCheckedChange={(v) => update('show_payment_info')(v)}
                data-testid="switch-payment-info"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_info_text">Texto da faixa de pagamentos</Label>
              <Input id="payment_info_text" value={form.payment_info_text} onChange={update('payment_info_text')} data-testid="input-payment-text" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="copyright_text">Texto de direitos reservados</Label>
              <Input
                id="copyright_text"
                placeholder="DESTACK VENDAS MAIS - Todos os direitos reservados"
                value={form.copyright_text}
                onChange={update('copyright_text')}
                data-testid="input-copyright"
              />
              <p className="text-xs text-slate-500">
                O ano atual ({new Date().getFullYear()}) será inserido automaticamente.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 sticky bottom-4 z-10 bg-white/95 backdrop-blur border border-slate-200 shadow-lg rounded-xl px-4 py-3">
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-footer-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar alterações
          </Button>
        </div>
      </form>

      {/* Live preview */}
      <Card className="border-slate-200" data-testid="footer-preview-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            Pré-visualização
          </CardTitle>
          <CardDescription>É exatamente assim que o rodapé aparece no site público.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-hidden rounded-b-lg">
            <SiteFooter key={refreshKey} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FooterSettings;
